import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs/Rx';
import { NetViewDto, NetViewTreeDto, PrepareNetworkTransferResultDto, NetworkTransferResultDto } from 'UvExplorerServer.Dtos.TS/NetViews';
import { AgentDto } from "UvExplorerServer.Dtos.TS/Agents";
import { NetViewService } from "./netview.service";
import { NetViewWebService } from "./netview.web.service";
import { ShareService } from "share";
import { DiscoverySettings, ScheduledDiscovery, DiscoveryMethod, DiscoverySubtaskHandle } from '../../DiscoveryEngine.TS/Discovery';
import { ShareDto, GetSharesResultDto } from '../../UvExplorerServer.Dtos.TS';
import { ScheduledDiscoveryStatusDto } from '../../UvExplorerServer.Dtos.TS/Discovery';
import { Guid, ScheduleType, StringUtils, ValueEncoder } from 'DiscoveryEngine.TS/Utils';
import { IdentityService } from 'identity';
import { DiscoveryTaskHandle } from '../../DiscoveryEngine.TS/DiscoveryService';
import { ConfigurationService } from '../../configuration';
import { IntegrationNames } from '../../DiscoveryEngine.TS/Core/Integration';


@Injectable()
export class NetFacadeService {

	constructor(private netViewService: NetViewService,
		private configurationService: ConfigurationService,
		private shareService: ShareService,
		private netViewWebService: NetViewWebService,
		private identityService: IdentityService) {

		return;
	}

	public get netviews(): NetViewDto[] {
		return this.netViewService.netviews;
	}

	public get rootForCurrentUser(): NetViewDto {
		return this.netViewService.rootForCurrentUser;
	}

	public get networks(): NetViewDto[] {
		return this.netViewService.networks;
	}

	public get agents(): AgentDto[] {
		return this.netViewService.agents;
	}

	public get onNetViewTreeLoaded(): Observable<any> {
		return this.netViewService.onNetViewTreeLoaded;
	}

	private get netViewTreeLoaded(): Subject<any> {
		return this.netViewService.netViewTreeLoaded;
	}

	private get netViewTreeChanged(): Subject<any> {
		return this.netViewService.netViewTreeChanged;
	}

	public get onNetViewTreeChanged(): Observable<any> {
		return this.netViewService.onNetViewTreeChanged;
	}

	public get onNetViewTreeUnloaded(): Observable<any> {
		return this.netViewService.onNetViewTreeUnloaded;
	}

	private get netViewTreeUnloaded(): Subject<any> {
		return this.netViewService.netViewTreeUnloaded;
	}

	public get isNetViewTreeLoaded(): boolean {
		return this.netViewService.isNetViewTreeLoaded;
	}

	public getNetViewTree(dataSelector: string): Observable<NetViewTreeDto> {
		return this.netViewService.getNetViewTree(dataSelector);
	}

	public reloadNetViewTree(): Observable<NetViewTreeDto> {
		return this.netViewService.reloadNetViewTree();
	}

	public getNetViewFromTree(guid: string): NetViewDto {
		return this.netViewService.getNetViewFromTree(guid);
	}

	public getAgentFromTree(guid: string): AgentDto {
		return this.netViewService.getAgentFromTree(guid);
	}

	public getNetViewFromServer(guid: string): Observable<NetViewDto> {
		return this.netViewService.getNetViewFromServer(guid);
	}

	public getAgentFromServer(agentGuid: string): Observable<AgentDto> {
		return this.netViewService.getAgentFromServer(agentGuid);
	}

	public createNetwork(name: string, description: string): Observable<NetViewDto> {
		let newNetwork = this.createNetViewDto(name, description);
		let subject = new Subject<NetViewDto>();

		this.netViewWebService.createNetView(newNetwork, this.rootForCurrentUser.Guid, -1, true).subscribe(
			(result: NetViewDto) => {
				result.Owner = this.identityService.user.Username;
				this.netViewService.addNetViewToTree(result);
				subject.next(result);
				this.netViewTreeChanged.next(true);
			},
			(error?) => {
				console.log(error);
				subject.error(error);
			});

		return subject.asObservable();
	}

	public updateNetView(network: NetViewDto): Observable<NetViewDto> {
		let subject = new Subject<NetViewDto>();

		this.netViewWebService.updateNetView(network).subscribe(
			(result: NetViewDto) => {
				if (this.isSharedNetwork(network.Guid)) {
					subject.next(result);
					this.sharesChanged.next(true);
				}
				else {
					subject.next(result);
					this.netViewTreeChanged.next(true);
				}
			},
			(error?) => {
				console.log(error);
				subject.error(error);
			});

		return subject.asObservable();
	}

	public deleteNetView(guid: string): Observable<boolean> {
		let parent: NetViewDto = this.netViewService.getNetViewParent(guid);
		let subject = new Subject<boolean>();

		this.netViewWebService.deleteNetViewByGuid(guid, parent.Guid).subscribe(
			(result: boolean) => {
				this.netViewService.removeNetViewFromTree(guid);
				subject.next(result);
				this.netViewTreeChanged.next(true);
			},
			(error?) => {
				console.error(error);
				subject.error(error);
			});

		return subject.asObservable();
	}

	public prepareNetworkTransfer(scopeGuid: string, oldUserGuid: string, newUserGuid: string):
		Observable<PrepareNetworkTransferResultDto> {
		return this.netViewWebService.prepareNetworkTransfer(scopeGuid, oldUserGuid, newUserGuid);
	}

	public doNetworkTransfer(scopeGuid: string, oldUserGuid: string, newUserGuid: string,
		apiKeyGuids: string[]): Observable<NetworkTransferResultDto> {
		return this.netViewWebService.doNetworkTransfer(scopeGuid, oldUserGuid, newUserGuid, apiKeyGuids);
	}

	public createAgent(name: string, description: string, parent: NetViewDto): Observable<AgentDto> {
		let scopeGuid: string = parent.Guid;
		let newAgent: AgentDto = this.createAgentDto(name, description, scopeGuid);
		let subject = new Subject<AgentDto>();

		this.netViewWebService.createAgent(newAgent, parent.Guid, -1).subscribe(
			(result: AgentDto) => {
				if (this.isSharedNetwork(parent.Guid)) {
					this.shareService.addTargetAgent(result, parent);
					subject.next(result);
					this.sharesChanged.next(true);
				}
				else {
					this.netViewService.addAgentToTree(result, parent);
					subject.next(result);
					this.netViewTreeChanged.next(true);
				}
			},
			(error?) => {
				console.error(error);
				subject.error(error);
			});

		return subject.asObservable();
	}

	public updateAgent(agent: AgentDto): Observable<AgentDto> {
		let subject = new Subject<AgentDto>();

		this.netViewWebService.updateAgent(agent).subscribe(
			(result: AgentDto) => {
				if (this.isSharedAgent(agent.Guid)) {
					subject.next(result);
					this.sharesChanged.next(true);
				}
				else {
					subject.next(result);
					this.netViewTreeChanged.next(true);
				}
			},
			(error?) => {
				console.log(error);
				subject.error(error);
			});

		return subject.asObservable();
	}

	public deleteAgent(guid: string): Observable<boolean> {
		let parent: NetViewDto = this.findAgentParent(guid);
		let subject = new Subject<boolean>();

		this.netViewWebService.deleteAgentByGuid(guid, parent.Guid).subscribe(
			(result: boolean) => {
				if (this.isSharedAgent(guid)) {
					this.shareService.removeTargetAgent(guid);
					subject.next(result);
					this.sharesChanged.next(true);
				}
				else {
					this.netViewService.removeAgentFromTree(guid);
					subject.next(result);
					this.netViewTreeChanged.next(true);
				}
			},
			(error?) => {
				console.error(error);
				subject.error(error);
			});

		return subject.asObservable();
	}

	public unbindAgent(agent: AgentDto): Observable<boolean> {
		return this.netViewService.unbindAgent(agent);
	}

	public dumpAgentConfig(guid: string): Observable<boolean> {
		return this.netViewWebService.dumpAgentConfig(guid);
	}

	public createScheduledDiscovery(name: string, parent: AgentDto): Observable<ScheduledDiscovery> {
		let now = new Date();

		let subject = new Subject<ScheduledDiscovery>();

		let discSettings = new DiscoverySettings();
		discSettings.VerifyGuid();
		discSettings.CreatedTime = ValueEncoder.EncodeDateTime(now);
		discSettings.ModifiedTime = ValueEncoder.EncodeDateTime(now);
		discSettings.Name = name + " Discovery Settings";
		discSettings.Method = DiscoveryMethod.PingSweep;

		if (parent != null) {
			var netViewDto = this.findNetwork(parent.ScopeGuid);
			if (netViewDto != null) {
				for (var i = 0; i < netViewDto.ProtocolSettings.Count; i++) {
					var protoSettings = netViewDto.ProtocolSettings.Get(i);
					if (protoSettings) {
						if (protoSettings.IsDefault) {
							discSettings.ProtocolSettings.AddProtoSettingsID(new Guid(protoSettings.GUID));
						}
					}
				}
			}
		}

		let newDisc = new ScheduledDiscovery();
		newDisc.VerifyGuid();
		newDisc.CreatedTime = ValueEncoder.EncodeDateTime(now);
		newDisc.ModifiedTime = ValueEncoder.EncodeDateTime(now);
		newDisc.Name = name;
		newDisc.DiscSettingsId = new Guid(discSettings.GUID);
		// Run every day at midnight
		newDisc.Schedule.Type = ScheduleType.Days_Interval;
		newDisc.Schedule.OneInterval = 1;
		newDisc.Schedule.OneStartTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
		newDisc.Enabled = true;
		newDisc.PrtgExport.ExportOrigin = "agent";
		newDisc.PrtgExport.ExportMasterNetworkOnly = true;

		//Set the export configs
		if (this.configurationService.hasIntegrationTable) {
			const table = this.configurationService.integrationTable;

			//ITGlue
			const itGlueIntegration = table.Find(IntegrationNames.ITGlue);
			if (!!itGlueIntegration) {
				const itGlueExport = newDisc.ITGlueExport;
				//itGlueExport.TargetServerURL = itGlueIntegration.ServerURL;
				itGlueExport.TargetServerApiURL = itGlueIntegration.ApiServerURL;
				itGlueExport.TargetServerApiKey = itGlueIntegration.ApiKey;
			}

			//Halo
			const haloIntegration = table.Find(IntegrationNames.Halo);
			if (!!haloIntegration) {
				const haloExport = newDisc.HaloExport;
				haloExport.TargetServerApiURL = haloIntegration.ServerURL;
				haloExport.TargetServerUsername = haloIntegration.Username;
				haloExport.TargetServerPassword = haloIntegration.Password;
			}

			//Hudu
			const huduIntegration = table.Find(IntegrationNames.Hudu);
			if (!!huduIntegration) {
				const huduExport = newDisc.HuduExport;
				huduExport.TargetServerApiURL = huduIntegration.ServerURL;
				huduExport.TargetServerApiKey = huduIntegration.ApiKey;
			}

			//Asset Panda
			const assetPandaIntegration = table.Find(IntegrationNames.AssetPanda);
			if (!!assetPandaIntegration) {
				const assetPandaExport = newDisc.AssetPandaExport;
				assetPandaExport.TargetServerApiURL = assetPandaIntegration.ApiServerURL;
				assetPandaExport.TargetServerUsername = assetPandaIntegration.Username;
				assetPandaExport.TargetServerPassword = assetPandaIntegration.Password;
			}

			//Jira
			const jiraIntegration = table.Find(IntegrationNames.Jira);
			if (!!jiraIntegration) {
				const jiraExport = newDisc.JiraExport;
				jiraExport.TargetServerURL = jiraIntegration.ServerURL;
				jiraExport.TargetServerUsername = jiraIntegration.Username;
				jiraExport.TargetApiToken = jiraIntegration.ApiKey;
			}
		}

		//<TEMP>
		//newDisc.Enabled = true;
		// Run every 3 minutes
		//newDisc.Schedule.Type = ScheduleType.Minutes_Interval;
		//newDisc.Schedule.OneInterval = 3;
		// Run every day at midnight
		//newDisc.Schedule.Type = ScheduleType.Days_Interval;
		//newDisc.Schedule.OneInterval = 1;
		//let now = new Date();
		//newDisc.Schedule.OneStartTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
		//newDisc.Keep = 3;
		//newDisc.LogEvents = true;
		//</TEMP>

		this.netViewWebService.createScheduledDiscovery(parent, newDisc, discSettings).subscribe(
			(result: AgentDto) => {
				subject.next(newDisc);
				if (this.isSharedAgent(parent.Guid))
					this.sharesChanged.next(true);
				else
					this.netViewTreeChanged.next(true);
			},
			(error?) => {
				console.error(error);
				subject.error(error);
			});

		return subject.asObservable();
	}

	public updateScheduledDiscovery(schedDisc: ScheduledDiscovery, parent: AgentDto): Observable<ScheduledDiscovery> {

		let now = new Date();

		let subject = new Subject<ScheduledDiscovery>();

		let discSettings: DiscoverySettings = null;
		if (schedDisc.HasDiscSettingsId) {
			let discSettingsGuid: string = schedDisc.DiscSettingsId.toString();
			discSettings = this.findSchedDiscSettings(schedDisc.GUID, discSettingsGuid);
		}
		if (!discSettings) {
			return new Observable<ScheduledDiscovery>(observer => {
				observer.error("Empty or missing discovery settings");
			});
		}

		schedDisc.ModifiedTime = ValueEncoder.EncodeDateTime(now);
		discSettings.ModifiedTime = ValueEncoder.EncodeDateTime(now);

		this.netViewWebService.updateScheduledDiscovery(parent, schedDisc, discSettings).subscribe(
			(result: AgentDto) => {
				subject.next(schedDisc);
				if (this.isSharedAgent(parent.Guid))
					this.sharesChanged.next(true);
				else
					this.netViewTreeChanged.next(true);
			},
			(error?) => {
				console.error(error);
				subject.error(error);
			});

		return subject.asObservable();
	}

	public deleteScheduledDiscovery(guid: string, parent: AgentDto): Observable<any> {

		let sdToDelete: ScheduledDiscovery = null;
		let dsToDelete: DiscoverySettings = null;

		parent.ScheduledDiscoveries.Some((disc: ScheduledDiscovery, index: number) => {
			if (disc.GUID == guid) {
				sdToDelete = disc;
				return true;
			}
			else {
				return false;
			}
		});

		if (sdToDelete) {
			let dsGuidStr = sdToDelete.DiscSettingsId.ToString();
			parent.DiscoverySettings.Some((ds: DiscoverySettings, index: number) => {
				if (ds.GUID == dsGuidStr) {
					dsToDelete = ds;
					return true;
				}
				else {
					return false;
				}
			});
		}

		if (!!sdToDelete && !!dsToDelete) {
			// Update agent on the server
			let subject = new Subject<any>();

			this.netViewWebService.deleteScheduledDiscovery(parent, sdToDelete.GUID, dsToDelete.GUID).subscribe(
				(result: AgentDto) => {
					subject.next(true);
					if (this.isSharedAgent(parent.Guid))
						this.sharesChanged.next(true);
					else
						this.netViewTreeChanged.next(true);
				},
				(error?) => {
					console.error(error);
					subject.error(error);
				});

			return subject.asObservable();
		}
		else {
			let message = "Error";
			if (!sdToDelete) {
				message = "Invalid scheduled discovery GUID";
			}
			else if (!sdToDelete.HasDiscSettingsId) {
				message = "Empty discovery settings in scheduled discovery";
			}
			else if (!dsToDelete) {
				message = "Invalid discovery settings GUID";
			}
			return new Observable<any>(o => o.error(message));
		}
	}

	private createNetViewDto(name: string, description: string): NetViewDto {
		let newNetview = new NetViewDto();
		newNetview.Name = name;
		newNetview.Description = description;
		newNetview.RollupPrtgExport.ExportOrigin = "server";
		return newNetview;
	}

	private createAgentDto(name: string, description: string, scopeGuid: string): AgentDto {
		let newAgent = new AgentDto();
		newAgent.ScopeGuid = scopeGuid;
		newAgent.Name = name;
		newAgent.Description = description;
		newAgent.RollupPrtgExport.ExportOrigin = "server";

		//<temp>
		//newAgent.PollInterval = 30;
		//newAgent.DiscoveryStatusInterval = 5;
		//</temp>

		return newAgent;
	}

	public findNetwork(networkGuid: string): NetViewDto {
		let network: NetViewDto = this.netViewService.getNetViewFromTree(networkGuid);
		if (!network) {
			network = this.shareService.getTargetNetworkByGuid(networkGuid);
		}
		return network;
	}

	public findAgent(agentGuid: string): AgentDto {
		let agent: AgentDto = this.netViewService.getAgentFromTree(agentGuid);
		if (!agent) {
			agent = this.shareService.getTargetAgentByGuid(agentGuid);
		}
		return agent;
	}

	public findAgentParent(agentGuid: string): NetViewDto {
		let parent: NetViewDto = this.netViewService.getAgentParent(agentGuid);
		if (!parent) {
			parent = this.shareService.getTargetAgentParent(agentGuid);
		}
		return parent;
	}

	public findSchedDiscParent(schedDiscGuid: string): AgentDto {
		let agent: AgentDto = this.netViewService.getSchedDiscParent(schedDiscGuid);
		if (!agent) {
			agent = this.shareService.getTargetSchedDiscParent(schedDiscGuid);
		}
		return agent;
	}

	public findSchedDiscSettings(schedDiscGuid: string, discSettingsGuid: string): DiscoverySettings {
		let discSettings = this.netViewService.getSchedDiscSettings(schedDiscGuid, discSettingsGuid);
		if (!discSettings) {
			discSettings = this.shareService.getTargetSchedDiscSettings(schedDiscGuid, discSettingsGuid);
		}
		return discSettings;
	}

	public findProtocolSettingsReferences(psGuid: string): string[] {
		let netViewRefs: string[] = this.netViewService.getProtocolSettingsReferences(psGuid);
		let shareRefs: string[] = this.shareService.getTargetProtocolSettingsReferences(psGuid);
		return [...netViewRefs, ...shareRefs];
	}

	public runDiscoveryAsap(agent: AgentDto, schedDiscGuid: string): Observable<any> {
		return this.netViewWebService.runDiscoveryAsap(agent, schedDiscGuid);
	}

	public cancelDiscoveryAsap(agent: AgentDto, discTaskHandle: DiscoveryTaskHandle): Observable<any> {
		return this.netViewWebService.cancelDiscoveryAsap(agent, discTaskHandle);
	}

	public cancelSubtaskAsap(agent: AgentDto, discTaskHandle: DiscoveryTaskHandle, subtaskHandle: DiscoverySubtaskHandle): Observable<any> {
		return this.netViewWebService.cancelSubtaskAsap(agent, discTaskHandle, subtaskHandle);
	}

	public runMonitorAsap(agent: AgentDto, policyGuid: string): Observable<any> {
		return this.netViewWebService.runMonitorAsap(agent, policyGuid);
	}

	public clearMonitorPolicyHistoryAsap(agent: AgentDto, policyGuid: string): Observable<any> {
		return this.netViewWebService.clearMonitorPolicyHistoryAsap(agent, policyGuid);
	}

	public clearDeviceMonitorHistoryAsap(agent: AgentDto, deviceGuid: string): Observable<any> {
		return this.netViewWebService.clearDeviceMonitorHistoryAsap(agent, deviceGuid);
	}

	public getSchedDiscStatus(schedDiscGuid: string): Observable<ScheduledDiscoveryStatusDto> {
		return this.netViewWebService.getSchedDiscStatus(schedDiscGuid);
	}

	//public getAgentLastConfigPollTime(agent: AgentDto): Observable<TimestampDto> {
	//	return this.netViewWebService.getAgentLastConfigPollTime(agent);
	//}

	//public setSchedDiscStatus(schedDiscGuid: string, status: ScheduledDiscoveryStatusDto): Observable<UpdateResultDto> {
	//	return this.netViewWebService.setSchedDiscStatus(schedDiscGuid, status);
	//}

	public isSharedNetwork(networkGuid: string): boolean {
		return !!this.shareService.getTargetNetworkByGuid(networkGuid);
	}

	public isSharedAgent(agentGuid: string): boolean {
		return !!this.shareService.getTargetAgentByGuid(agentGuid);
	}

	public get areSharesLoaded(): boolean {
		return this.shareService.areSharesLoaded;
	}

	public get sourceShares(): ShareDto[] {
		return this.shareService.sourceShares;
	}

	public get targetShares(): ShareDto[] {
		return this.shareService.targetShares;
	}

	public get targetNetViews(): NetViewDto[] {
		return this.shareService.targetNetViews;
	}

	public get targetRootNetView(): NetViewDto {
		return this.shareService.targetRootNetView;
	}

	public get targetNetworks(): NetViewDto[] {
		return this.shareService.targetNetworks;
	}

	public get targetAgents(): AgentDto[] {
		return this.shareService.targetAgents;
	}

	private get sharesLoaded(): Subject<any> {
		return this.shareService.sharesLoaded;
	}

	public get onSharesLoaded(): Observable<any> {
		return this.shareService.onSharesLoaded;
	}

	private get sharesChanged(): Subject<any> {
		return this.shareService.sharesChanged;
	}

	public get onSharesChanged(): Observable<any> {
		return this.shareService.onSharesChanged;
	}

	private get sharesUnloaded(): Subject<any> {
		return this.shareService.sharesUnloaded;
	}

	public get onSharesUnloaded(): Observable<any> {
		return this.shareService.onSharesUnloaded;
	}

	public reloadShares(): Observable<any> {
		return this.shareService.reloadShares();
	}

	public getShares(): Observable<GetSharesResultDto> {
		return this.shareService.getShares();
	}

	public getTargetNetworkByGuid(guid: string): NetViewDto {
		return this.shareService.getTargetNetworkByGuid(guid);
	}

	public getTargetAgentByGuid(guid: string): AgentDto {
		return this.shareService.getTargetAgentByGuid(guid);
	}

	public getTargetAgentParent(agentGuid: string): NetViewDto {
		return this.shareService.getTargetAgentParent(agentGuid);
	}

	public getTargetSchedDiscParent(schedDiscGuid: string): AgentDto {
		return this.shareService.getTargetSchedDiscParent(schedDiscGuid);
	}

	public getSourceSharesForItem(sharedItemGuid: string): ShareDto[] {
		return this.shareService.getSourceSharesForItem(sharedItemGuid);
	}

	public getTargetSharesForItem(sharedItemGuid: string): ShareDto[] {
		return this.shareService.getTargetSharesForItem(sharedItemGuid);
	}

	public createShare(newShare: ShareDto): Observable<ShareDto> {
		return this.shareService.createShare(newShare);
	}

	public updateShare(share: ShareDto): Observable<ShareDto> {
		return this.shareService.updateShare(share);
	}

	public deleteSourceShare(guid: string): Observable<boolean> {
		return this.shareService.deleteSourceShare(guid);
	}

	public deleteTargetShare(guid: string): Observable<boolean> {
		return this.shareService.deleteTargetShare(guid);
	}

}